<script>
import find from 'lodash/find';
import storage from 'viralstyle-ui/src/storage';
import VsErrorPage from 'viralstyle-ui/src/VsErrorPage';
import VsLazyLoad from 'viralstyle-ui/src/VsLazyLoad';
import VsLogoLoader from 'viralstyle-ui/src/VsLogoLoader';
import { mapState, mapGetters } from 'vuex';

import mixins from '../../utils/mixins';
import loadingGif from '../../assets/images/ring.gif';
import Collection from '../../components/Collection';

export default {
  components: {
    VsErrorPage,
    VsLazyLoad,
    VsLogoLoader,
    Collection,
  },
  mixins: [mixins],
  props: {
    collectionSlug: String,
    page: String,
  },
  data: () => ({
    loadingGif,
    notFound: false,
  }),
  computed: {
    ...mapState([
      'processing',
      'currencyInfo',
      'isWhitelabel',
      'domainConfig',
      'collections',
      'isUserAdmin',
    ]),
    ...mapState({
      info: state => state.collection.info,
      campaigns: state => state.collection.campaigns,
      sortOptions: state => state.collection.sortOptions,
      selectedProductType: state => state.collection.selectedProductType,
      collectionProductTypes: state => state.collection.productTypes,
      maxPages: state => state.collection.maxPages,
      hasProductTypes: state => state.collection.productTypes,
      showMobileFilterModal: state => state.collection.showMobileFilterModal,
      showMobileSortModal: state => state.collection.showMobileSortModal,
    }),
    ...mapGetters([
      'collectionSelectedSortText',
      'collectionFormattedProductType',
      'collectionPages',
    ]),
    ready() {
      return this.info && this.campaigns && this.hasProductTypes;
    },
    violatingCopyright() {
      return this.ready && this.info.violates_copyright;
    },
    disabled() {
      return this.ready && this.info.disabled;
    },
    publicName() {
      return this.isWhitelabel ? this.domainConfig.public_name : this.$route.params.publicName;
    },
  },
  async beforeRouteUpdate(to, from, next) {
    await next();
    this.$_initialize();
  },
  async created() {
    this.$_initialize();
  },
  methods: {
    async $_initialize() {
      let q = this.$route.query;

      if (q.product_type) {
        this.$store.commit('updateState', { object: this.$store.state.collection, property: 'selectedProductType', value: q.product_type });
      }

      if (q.sort) {
        this.$store.commit('updateState', { object: this.$store.state.collection, property: 'selectedSort', value: find(this.sortOptions, option => option.value === q.sort).key });
      }

      this.$store.dispatch('getCollectionFilters');

      this.$store.commit('clearCollectionPartnerInfo');

      try {
        await this.$store.dispatch('getCollection', `${this.publicName}${this.collectionSlug}`);

        if (this.isWhitelabel && !find(this.collections, collection => collection.slug === this.collectionSlug)) {
          this.$router.push({ name: '404' });

          return false;
        }

        this.$_recordVisit();

        storage.set('last-listing-url', { time: new Date(), url: this.$route.fullPath });
      } catch (e) {
        this.notFound = true;
      }
    },
    async $_recordVisit() {
      if (!this.$_visitRecorded(this.publicName, `collection/${this.collectionSlug}`)) {
        await this.$store.dispatch('recordCollectionVisit');

        this.visitRecords[this.publicName].push({ sl: `collection/${this.collectionSlug}` });

        storage.set('visit-records', this.visitRecords, { storages: ['session'] });
      }
    },
    handleViewCollection() {
      this.$store.dispatch('viewCollection');
    },
    handleUpdateState({ property, value }) {
      this.$store.commit('updateState', { object: this.$store.state.collection, property, value });
    },
    handleGetCampaigns(page) {
      this.$store.dispatch('lazyLoadCollectionCampaigns', `${page || 1}`);
    },
    handleLockScroll(isLocked) {
      this.$store.commit('lockScroll', isLocked);
    },
  },
  metaInfo() {
    let meta = [],
      title = 'Collection';

    if (this.info) {
      meta.push({ property: 'og:description', content: this.$_removeTags(this.info.description) });
      meta.push({ property: 'og:title', content: this.info.name });
      meta.push({ property: 'og:image', content: 'https://s3.amazonaws.com/viral-tees/og-store-logo.jpg' });

      title = `${title} - ${this.info.name}`
    }

    return {
      title,
      meta,
    };
  },
};
</script>

<template lang='pug'>
transition(name='body-loader')
  vs-error-page(
    v-if='violatingCopyright',
    heading='Copyright Violation',
    message='This collection has been removed in response to a report of potential infringement of intellectual property rights and is no longer taking orders.',
  )
  vs-error-page(
    v-else-if='disabled',
    heading='Collection Offline',
    message='This collection has been disabled by the creator.',
  )
  collection(
    v-else-if='ready'
    route-name='collection',
    :info='info',
    :campaigns='campaigns',
    :currency-info='currencyInfo',
    :page='page',
    :is-whitelabel='isWhitelabel',
    :processing='processing',
    :sort-options='sortOptions',
    :selected-sort-text='collectionSelectedSortText',
    :selected-product-type='selectedProductType',
    :formatted-product-type='collectionFormattedProductType',
    :product-types='collectionProductTypes',
    :pages='collectionPages',
    :max-pages='maxPages',
    :is-user-admin='isUserAdmin',
    :show-sort-and-filter='info.enable_sort_and_filter',
    :alternate-image='isWhitelabel ? domainConfig.assets.banner : null',
    :coupon='$route.query.coupon',
    :show-mobile-filter-modal='showMobileFilterModal',
    :show-mobile-sort-modal='showMobileSortModal',
    @view-collection='handleViewCollection',
    @update-state='handleUpdateState',
    @get-campaigns='handleGetCampaigns',
    @lock-scroll='handleLockScroll',
  )
  vs-error-page(
    v-else-if='notFound',
    heading='404 Collection Not Found',
    message='This collection does not exist or the URL has been changed by the creator.',
  )
  .body-padding(v-else)
    img.opacity-100.mx-auto(:src='loadingGif', alt='loading')
</template>

<style>
@import '~import/index';

.lazy-load__container {
  .logo-loader {
    height: 7.5rem;
  }
}
</style>